import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { requestNavigation } from '../utils/navigationGuard';
import './ProductSidebar.css';

/**
 * Launchpad — fixed left rail giving one-click access to every product page.
 * Collapsed state shows all 10 product icons stacked (no extra click needed
 * to see what's available). Expands on hover to reveal labels, grouped by
 * category. Click a row to navigate (or open the auth modal if logged out).
 */

const GROUPS = [
  {
    label: 'Practice',
    items: [
      { icon: '🏆', label: 'Interview Assistant', path: '/InterviewAssistant', accent: '#e0a3ff' },
      { icon: '🔮', label: 'Mock Interview',      path: '/mockInterview',      accent: '#ff69b4' },
      { icon: '💎', label: 'Interview Questions', path: '/InterviewQuestions', accent: '#9370db' },
    ],
  },
  {
    label: 'Build',
    items: [
      { icon: '📄', label: 'Resume Builder',     path: '/resume-builder', accent: '#00ffff' },
      { icon: '🛡️', label: 'Cover Letter',       path: '/CoverLetter',    accent: '#ffd166' },
      { icon: '🌐', label: 'Portfolio Builder',  path: '/portfolio',      accent: '#a78bfa' },
    ],
  },
  {
    label: 'Optimize',
    items: [
      { icon: '⭐', label: 'ATS Check',          path: '/ATSCheck',          accent: '#7ee8a3' },
      { icon: '📈', label: 'LinkedIn Optimizer', path: '/LinkedInOptimizer', accent: '#74c0fc' },
    ],
  },
  {
    label: 'Apply & Learn',
    items: [
      { icon: '⚡', label: 'Auto Apply',         path: '/AutoJobApply',  accent: '#ff8c69' },
      { icon: '🎓', label: 'AI Certification',  path: '/certification', accent: '#fbbf77' },
    ],
  },
];

const ProductSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Launchpad tiles take everyone — including signed-out visitors — straight
  // to the product's landing page. The landing pages handle their own auth
  // prompts at the moment of an action that actually requires an account
  // (e.g. starting a session, deducting credits). Gating the rail itself was
  // a deterrent: visitors couldn't even browse what the product does.
  const handleClick = (path) => {
    // Route through the global guard so the live-interview page can pop
    // its exit-confirmation modal before any navigation actually happens.
    // When no guard is installed, this just calls navigate(path) directly.
    requestNavigation(path, (target) => {
      navigate(target);
      window.scrollTo(0, 0);
    });
  };

  return (
    <aside className="launchpad" aria-label="Product launchpad">
      <div className="lp-rail">
          <div className="lp-brand">
            <span className="lp-brand-icon" aria-hidden="true">⚡</span>
            <span className="lp-brand-text">Launchpad</span>
          </div>

          {GROUPS.map((group) => (
            <div className="lp-group" key={group.label}>
              <div className="lp-group-label">{group.label}</div>
              <div className="lp-group-items">
                {group.items.map((item) => {
                  const active = location.pathname === item.path;
                  return (
                    <button
                      key={item.path}
                      type="button"
                      className={`lp-item ${active ? 'is-active' : ''}`}
                      onClick={() => handleClick(item.path)}
                      style={{ '--lp-accent': item.accent }}
                      title={item.label}
                    >
                      <span className="lp-item-icon" aria-hidden="true">{item.icon}</span>
                      <span className="lp-item-label">{item.label}</span>
                      {active && <span className="lp-active-bar" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="lp-foot">
            <span className="lp-foot-dot" aria-hidden="true" />
            <span className="lp-foot-text">TERVIEW.AI</span>
          </div>
        </div>
    </aside>
  );
};

export default ProductSidebar;
